// src/utils/mockDataClientSpecs.ts
// Client specification library — TMR MRTS, Energex TECs, AS/NZS standards
// Sourced from: https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its

import { WIDiscipline } from './mockDataWorkInstructions'

// ── Types ─────────────────────────────────────────────────────────

export type SpecSource  = 'TMR' | 'ENERGEX' | 'AS_NZS' | 'COUNCIL' | 'INTERNAL'
export type SpecStatus  = 'CURRENT' | 'SUPERSEDED' | 'DRAFT'
export type TradeCode   = 'EL1' | 'EL2' | 'EL3' | 'HV' | 'CL1' | 'CL2' | 'TC' | 'SV'

export interface TradeRate {
  code: TradeCode
  label: string
  description: string
  hourlyRateLoaded: number   // $ per hour — inclusive of on-costs, super, insurance, overhead
}

export interface ClientSpec {
  id: string
  reference: string          // e.g. 'MRTS93', 'ENX-TEC-2241', 'AS/NZS 3000'
  title: string
  version: string            // e.g. 'July 2024', 'Rev 4'
  source: SpecSource
  discipline: WIDiscipline
  status: SpecStatus
  sourceUrl?: string         // authoritative published URL (public specs)
  hasUploadedFile: boolean   // whether a PDF/DOCX has been uploaded to the platform
  uploadedFileName?: string
  uploadedAt?: string
  importedAt: string
  lastReviewedAt?: string
  updateAvailableFlag?: boolean  // true when URL-linked spec has a newer version detected
  description: string
  keyRequirements: string[]       // plain-language methodology requirements
  relatedSpecIds: string[]        // cross-references to other specs in the library
  linkedSopIds: string[]          // WI SOPs that implement this spec
  importedById: string
  importedByName: string
}

// ── Trade rates ───────────────────────────────────────────────────

export const TRADE_RATES: TradeRate[] = [
  { code: 'EL1', label: 'Electrical apprentice',       description: 'Years 1–2. Supervised tasks only.',                                      hourlyRateLoaded: 82  },
  { code: 'EL2', label: 'Electrician (EL licence)',    description: 'Standard electrical installation tasks. Independent site work.',         hourlyRateLoaded: 118 },
  { code: 'EL3', label: 'Licensed Electrical Inspector',description: 'LV inspection, final test, compliance certification.',                   hourlyRateLoaded: 145 },
  { code: 'HV',  label: 'HV Authorised Person',        description: 'HV switching, HVSP execution, 33kV jointing. Energex AP required.',     hourlyRateLoaded: 178 },
  { code: 'CL1', label: 'Civil labourer',              description: 'Traffic management, excavation assist, compaction, concrete assist.',    hourlyRateLoaded: 74  },
  { code: 'CL2', label: 'Civil leading hand',          description: 'Concrete pours, survey set-out, civil supervision, compaction testing.', hourlyRateLoaded: 92  },
  { code: 'TC',  label: 'Traffic Controller',          description: 'TCP duties only. Queensland TMR Traffic Controller accreditation.',     hourlyRateLoaded: 88  },
  { code: 'SV',  label: 'Site supervisor / PC',        description: 'Hold point inspections, WHS compliance, coordination, sign-off.',       hourlyRateLoaded: 110 },
]

export const TRADE_RATE_MAP: Record<TradeCode, TradeRate> =
  Object.fromEntries(TRADE_RATES.map(r => [r.code, r])) as Record<TradeCode, TradeRate>

export function calcStepCost(hours: number, tradeCode: TradeCode): number {
  return Math.round(hours * TRADE_RATE_MAP[tradeCode].hourlyRateLoaded * 100) / 100
}

// ── Display metadata ───────────────────────────────────────────────

export const SOURCE_META: Record<SpecSource, { label: string; bg: string; text: string; dot: string }> = {
  TMR:      { label: 'TMR',              bg: 'bg-blue-100',   text: 'text-blue-800',   dot: 'bg-blue-500' },
  ENERGEX:  { label: 'Energex',          bg: 'bg-amber-100',  text: 'text-amber-800',  dot: 'bg-amber-500' },
  AS_NZS:   { label: 'AS/NZS Standard',  bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  COUNCIL:  { label: 'Council',          bg: 'bg-teal-100',   text: 'text-teal-800',   dot: 'bg-teal-500' },
  INTERNAL: { label: 'Internal',         bg: 'bg-gray-100',   text: 'text-gray-700',   dot: 'bg-gray-400' },
}

export const SPEC_STATUS_META: Record<SpecStatus, { label: string; bg: string; text: string }> = {
  CURRENT:    { label: 'Current',    bg: 'bg-green-100',  text: 'text-green-700' },
  SUPERSEDED: { label: 'Superseded', bg: 'bg-gray-100',   text: 'text-gray-400' },
  DRAFT:      { label: 'Draft',      bg: 'bg-amber-100',  text: 'text-amber-700' },
}

// ── TMR MRTS Specifications ────────────────────────────────────────

export const MOCK_CLIENT_SPECS: ClientSpec[] = [

  // ── Core civil / structural ──────────────────────────────────────

  {
    id: 'spec-mrts91', reference: 'MRTS91', title: 'Conduits and Pits',
    version: 'July 2024', source: 'TMR', discipline: 'CIVIL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: true, uploadedFileName: 'MRTS91-Conduits-Pits-July2024.pdf', uploadedAt: '2026-02-10T09:00:00Z',
    importedAt: '2026-02-10T09:00:00Z', lastReviewedAt: '2026-03-01T10:00:00Z',
    description: 'Specifies requirements for the installation of conduits, cable pits, and associated infrastructure in TMR road corridors. Covers UPVC, HDPE and steel conduit types, pit construction, pit sealing, conduit entry methods, and installation tolerances.',
    keyRequirements: [
      'Conduit minimum cover depth: 600mm under trafficked areas, 450mm under footpaths',
      'All pit lids must be rated to trafficable load (AASHTO H20 minimum in road reserve)',
      'Conduit entries must be sealed with approved sealant compound on both faces',
      'Pit base must have drainage (crushed rock or gravel bed — minimum 100mm)',
      'Maximum conduit run without a pit: 60m for underground, 40m for directional bore',
      'Conduit installed by directional bore must be HDPE, minimum Class 12',
    ],
    relatedSpecIds: ['spec-mrts92', 'spec-mrts256'],
    linkedSopIds: ['wi-sop-002'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts92', reference: 'MRTS92', title: 'Traffic Signal and Road Lighting Footings',
    version: 'March 2018', source: 'TMR', discipline: 'CIVIL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: true, uploadedFileName: 'MRTS92-Footings-March2018.pdf', uploadedAt: '2026-02-10T09:00:00Z',
    importedAt: '2026-02-10T09:00:00Z', lastReviewedAt: '2026-03-01T10:00:00Z',
    description: 'Defines design and construction requirements for footings supporting traffic signal poles, luminaire poles, and associated roadside mounting structures. References AS 3600 for concrete design.',
    keyRequirements: [
      'Concrete grade: minimum Grade 32 (25MPa at 7 days). Grade 40 specified for poles over 8m.',
      'DIN base plate must be set to ±5mm in plan and ±2mm in level',
      'Bolt projection above concrete: as specified on the engineering drawing ±5mm',
      'Curing period: minimum 72 hours before pole erection',
      'Backfill compaction: ≥95% Standard Proctor, tested and recorded per lift',
      'Hold point required: PC/engineer to inspect at excavation depth before pour',
      'Footing design certified by RPEQ must be on file before construction commences',
    ],
    relatedSpecIds: ['spec-mrts91', 'spec-mrts93'],
    linkedSopIds: ['wi-sop-001'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  // ── Traffic signals ──────────────────────────────────────────────

  {
    id: 'spec-mrts93', reference: 'MRTS93', title: 'Traffic Signals',
    version: 'July 2024', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: true, uploadedFileName: 'MRTS93-Traffic-Signals-July2024.pdf', uploadedAt: '2026-03-01T09:00:00Z',
    importedAt: '2026-03-01T09:00:00Z', lastReviewedAt: '2026-06-01T10:00:00Z',
    updateAvailableFlag: false,
    description: 'Primary specification governing the complete installation of traffic signal systems in Queensland, including signal heads, poles, controllers, wiring, and commissioning. This is the master specification for MCB CE signal installation work on TMR projects.',
    keyRequirements: [
      'All equipment must be supplied from TMR Type Approved Equipment Register (TAER)',
      'Signal head aim: nominally aimed at stop line, ±0.5° tilt from vertical',
      'Controller fit-out must comply with MRTS255 (signal controllers) and MRTS252 (NGSC)',
      'SCATS configuration file must be supplied by TMR ITS Branch — contractor must not modify',
      'Commissioning hold point: TMR ITS inspector must witness functional test',
      'All conduit and pit works must comply with MRTS91',
      'All footings must comply with MRTS92',
      'Cable selection and installation per MRTS256 (power) and MRTS257 (detector loops)',
      'Warranty period: 12 months from date of commissioning acceptance',
    ],
    relatedSpecIds: ['spec-mrts91', 'spec-mrts92', 'spec-mrts252', 'spec-mrts253', 'spec-mrts255', 'spec-mrts256'],
    linkedSopIds: ['wi-sop-004', 'wi-sop-005'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts252', reference: 'MRTS252', title: 'Next Generation Traffic Signal Controllers',
    version: 'November 2019', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-03-10T09:00:00Z',
    description: 'Specifies requirements for the supply, installation, and commissioning of Next Generation Signal Controllers (NGSC) on TMR projects. Covers firmware, communication protocols, and SCATS interface.',
    keyRequirements: [
      'Controllers must be from TMR TAER (Type Approved Equipment Register)',
      'SCATS V8 communication protocol required',
      'Pre-energisation inspection hold point (EL2 or above)',
      'SCATS configuration must be loaded from TMR-supplied file — no contractor modification',
      'Communication link to TMR regional server must be confirmed before sign-off',
    ],
    relatedSpecIds: ['spec-mrts93', 'spec-mrts255'],
    linkedSopIds: ['wi-sop-005'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts253', reference: 'MRTS253', title: 'Traffic Signal Lanterns',
    version: 'July 2023', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-03-10T09:00:00Z',
    description: 'Requirements for LED traffic signal lantern types, mounting configurations, alignment, and photometric performance on TMR road networks.',
    keyRequirements: [
      'LED lanterns must be from TMR TAER — no substitution without written approval',
      'Horizontal aim: nominally at the stop line from the signal face',
      'Vertical tilt: ≤0.5° from vertical (measured with inclinometer)',
      'Bracket torque: per manufacturer specification — torque value recorded',
      'Post-installation IR test of all circuits before energisation',
    ],
    relatedSpecIds: ['spec-mrts93', 'spec-mrts255'],
    linkedSopIds: ['wi-sop-004'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts255', reference: 'MRTS255', title: 'Traffic Signal Controllers',
    version: 'November 2020', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-03-10T09:00:00Z',
    description: 'Specifies requirements for the installation, wiring, and commissioning of traffic signal controller cabinets, including field wiring termination and sign-off protocols.',
    keyRequirements: [
      'All field wiring to be terminated per the job-specific wiring schedule',
      'Cable labels at both ends — circuit reference per drawing',
      'Terminal torque per manufacturer specification',
      'Mains supply: minimum 20A protected circuit from approved supply point',
      'Pre-energisation hold point: PM or EL2 must verify wiring before energisation',
    ],
    relatedSpecIds: ['spec-mrts93', 'spec-mrts252'],
    linkedSopIds: ['wi-sop-005'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  // ── Cables ───────────────────────────────────────────────────────

  {
    id: 'spec-mrts256', reference: 'MRTS256', title: 'Power Cables',
    version: 'July 2023', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-04-01T09:00:00Z',
    description: 'Selection, installation, and testing requirements for power cables used in traffic signal and road lighting installations.',
    keyRequirements: [
      'Cable type: AS/NZS 3008 compliant multi-core copper — minimum 6mm² for feeder circuits',
      'Maximum conduit fill: 40% (AS/NZS 3000 cl. 3.4)',
      'Maximum cable pull tension per manufacturer specification — record tension on WI',
      'Conduit entries sealed with approved compound at all pits',
      'Post-pull IR test: minimum 100 MΩ at 500V DC between each conductor and earth',
      'Service loop minimum 1m at each termination point',
    ],
    relatedSpecIds: ['spec-mrts91', 'spec-mrts93'],
    linkedSopIds: ['wi-sop-003', 'wi-sop-006'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts234', reference: 'MRTS234', title: 'Communications Cables',
    version: 'March 2024', source: 'TMR', discipline: 'COMMISSIONING', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-04-15T09:00:00Z',
    updateAvailableFlag: false,
    description: 'Specifies requirements for fibre optic and copper communications cables in ITS infrastructure, including installation, splicing, and OTDR testing.',
    keyRequirements: [
      'Fibre optic cable: OS2 single-mode (9/125μm) minimum for backbone runs',
      'Maximum splice insertion loss: 0.1 dB per splice (fusion splice)',
      'OTDR test required from both ends of each installed run — results retained',
      'Minimum bend radius: 20× cable diameter during installation, 10× at rest',
      'Fibre sleeves and splice trays: rated for outdoor use if in an outdoor pit',
    ],
    relatedSpecIds: ['spec-mrts245', 'spec-mrts200'],
    linkedSopIds: ['wi-sop-013'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  // ── ITS / infrastructure ─────────────────────────────────────────

  {
    id: 'spec-mrts200', reference: 'MRTS200', title: 'General Requirements for Intelligent Transport Systems Infrastructure',
    version: 'July 2017', source: 'TMR', discipline: 'COMMISSIONING', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-04-10T09:00:00Z',
    description: 'General overarching requirements applicable to all ITS infrastructure installations on the TMR network. Referenced by all specific ITS specs.',
    keyRequirements: [
      'All equipment to be from TMR Type Approved Equipment Register unless otherwise approved in writing',
      'Contractor must hold current TMR ITS System Accreditation for the relevant system',
      'All works subject to TMR ITS Inspector witness and acceptance',
      'As-constructed drawings must be submitted within 28 days of practical completion',
    ],
    relatedSpecIds: ['spec-mrts93', 'spec-mrts234', 'spec-mrts245'],
    linkedSopIds: ['wi-sop-011'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts210', reference: 'MRTS210', title: 'Provision of Mains Power',
    version: 'November 2021', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-03-15T09:00:00Z',
    description: 'Requirements for mains power connection to traffic signal and ITS equipment, including metering, protection, and Energex connection standards.',
    keyRequirements: [
      'Mains supply must be connected to an Energex-approved Revenue Metering Point',
      'Protective earth must comply with AS/NZS 3000 Wiring Rules',
      'Maximum supply circuit protection: 32A MCB at the Energex connection',
      'All mains connection works require a licensed electrician (EL2 minimum)',
    ],
    relatedSpecIds: ['spec-mrts93'],
    linkedSopIds: ['wi-sop-006'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts211', reference: 'MRTS211', title: 'Pedestrian Push Buttons and Audio Tactile Drivers',
    version: 'October 2017', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-03-15T09:00:00Z',
    description: 'Specifies push button and audio tactile driver (ATD) installation for pedestrian crossings, including mounting height, orientation, and commissioning test.',
    keyRequirements: [
      'Push button centre height: 900–1200mm above finished footpath level',
      'ATD must face the pedestrian crossing direction to within ±15°',
      'Commissioning test: confirm push button activates demand and ATD sounds on green phase',
    ],
    relatedSpecIds: ['spec-mrts93'],
    linkedSopIds: [],
    importedById: 'u4', importedByName: 'Sarah Lee',
  },

  {
    id: 'spec-mrts245', reference: 'MRTS245', title: 'ITS Telecommunications Network (ITS TN)',
    version: 'July 2020', source: 'TMR', discipline: 'COMMISSIONING', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-04-20T09:00:00Z',
    description: 'RESTRICTED: ITS telecommunications network specification. Available to authorised contractors via TMR PM or Commercial Manager. Contact: [email protected].',
    keyRequirements: [
      'RESTRICTED document — not publicly available. Request via nominated TMR PM.',
      'Covers ITS fibre backbone and communications network requirements.',
      'All contractors must be specifically nominated in the contract to receive this document.',
    ],
    relatedSpecIds: ['spec-mrts234', 'spec-mrts200'],
    linkedSopIds: [],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-mrts94', reference: 'MRTS94', title: 'Road Lighting',
    version: 'November 2023', source: 'TMR', discipline: 'ELECTRICAL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-04-10T09:00:00Z',
    description: 'Requirements for road lighting installations on TMR projects, including luminaire selection, mounting height, wiring, and commissioning.',
    keyRequirements: [
      'Luminaire selection from TMR TAER for lighting products',
      'Mounting height per AS/NZS 1158 — road lighting standard',
      'Photometric calculation report required as pre-construction submittal',
      'Night-time commissioning inspection required with TMR representative',
    ],
    relatedSpecIds: ['spec-mrts92', 'spec-mrts256'],
    linkedSopIds: [],
    importedById: 'u4', importedByName: 'Sarah Lee',
  },

  // ── Safety / confined space ──────────────────────────────────────

  {
    id: 'spec-mrts97', reference: 'MRTS97', title: 'Mounting Structures for Roadside Equipment',
    version: 'July 2022', source: 'TMR', discipline: 'CIVIL', status: 'CURRENT',
    sourceUrl: 'https://www.tmr.qld.gov.au/business-industry/technical-standards-publications/specifications/4-electrical-and-its',
    hasUploadedFile: false,
    importedAt: '2026-05-01T09:00:00Z',
    description: 'Specifies requirements for signal arms, cantilever arms, gantries, and other mounting structures for roadside ITS equipment.',
    keyRequirements: [
      'All structures must be RPEQ-certified — footing design must be signed off by engineer',
      'Hot-dip galvanising to AS 4680 for all structural steel components',
      'Maximum deflection under wind loading per AS/NZS 1170.2',
    ],
    relatedSpecIds: ['spec-mrts92', 'spec-mrts93'],
    linkedSopIds: [],
    importedById: 'u4', importedByName: 'Sarah Lee',
  },

  // ── Energex specs ────────────────────────────────────────────────

  {
    id: 'spec-enx-tec001', reference: 'ENX-TEC-001', title: 'HV Cable Jointing — Heat Shrink Method (33kV)',
    version: 'Rev 4 (2024)', source: 'ENERGEX', discipline: 'HV_ELECTRICAL', status: 'CURRENT',
    hasUploadedFile: true, uploadedFileName: 'ENX-TEC-001-Rev4-HV-Jointing.pdf', uploadedAt: '2026-01-20T09:00:00Z',
    importedAt: '2026-01-20T09:00:00Z', lastReviewedAt: '2026-02-01T09:00:00Z',
    description: 'Energex Technical Engineering Criterion for 33kV XLPE cable jointing using the heat shrink method. Mandatory for all HV cable joints on the Energex network.',
    keyRequirements: [
      'Jointing must be performed by an Energex-accredited HV cable jointer',
      'Joint kit: manufacturer-matched to cable type and voltage rating',
      'Heat application: temperature probe required — do not exceed manufacturer upper limit',
      'Post-joint test: HV test at 1.7U0 for 15 minutes minimum (DC or VLF)',
      'Partial discharge test below 10 pC at 1.0U0',
      'HVSP clearance must be in hand before any cable work commences',
      'Hold point: PM or accredited jointer to inspect joint before outer sheath application',
    ],
    relatedSpecIds: ['spec-enx-hvsw001'],
    linkedSopIds: ['wi-sop-007'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-enx-hvsw001', reference: 'ENX-HV-SWITCH', title: 'HV Switching Code — Parallel Isolation Procedure',
    version: 'Rev 6 (2023)', source: 'ENERGEX', discipline: 'HV_ELECTRICAL', status: 'CURRENT',
    hasUploadedFile: true, uploadedFileName: 'Energex-HV-Switching-Code-Rev6.pdf', uploadedAt: '2026-01-20T09:00:00Z',
    importedAt: '2026-01-20T09:00:00Z', lastReviewedAt: '2026-02-01T09:00:00Z',
    description: 'Energex HV Switching Code governing all HV switching operations and isolation procedures on the Energex distribution network.',
    keyRequirements: [
      'HVSP must be authorised by Energex System Control before any switching commences',
      'AP holder must not deviate from HVSP sequence — any deviation requires system control contact',
      'Prove dead before touch: HV voltage indicator test on known live source first',
      'Earth clamps must be applied at all specified earthing points before work commences',
      'Photographic evidence of earthing positions is mandatory',
    ],
    relatedSpecIds: ['spec-enx-tec001'],
    linkedSopIds: ['wi-sop-012'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  // ── AS/NZS Standards ────────────────────────────────────────────

  {
    id: 'spec-as3000', reference: 'AS/NZS 3000', title: 'Wiring Rules',
    version: '2018 (Amdt 2021)', source: 'AS_NZS', discipline: 'ELECTRICAL', status: 'CURRENT',
    hasUploadedFile: true, uploadedFileName: 'AS-NZS-3000-2018-Wiring-Rules.pdf', uploadedAt: '2026-02-01T09:00:00Z',
    importedAt: '2026-02-01T09:00:00Z',
    description: 'The principal Australian and New Zealand standard for electrical wiring installations. Referenced by all electrical SOPs and all TMR electrical specifications.',
    keyRequirements: [
      'Cable selection: conductor size per AS/NZS 3008.1.1 for the design current and installation method',
      'Conduit fill: maximum 40% of conduit cross-sectional area for multi-cable pulls (cl. 3.4)',
      'Earthing: all exposed conductive parts must be earthed per Section 5',
      'Protection: all circuits must have overload, short-circuit, and earth fault protection',
      'Testing: continuity, insulation resistance, polarity, earth fault loop impedance, and RCD tests required',
    ],
    relatedSpecIds: ['spec-mrts256', 'spec-mrts93'],
    linkedSopIds: ['wi-sop-003', 'wi-sop-006'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },

  {
    id: 'spec-as2865', reference: 'AS 2865', title: 'Safe Working in a Confined Space',
    version: '2009', source: 'AS_NZS', discipline: 'ALL', status: 'CURRENT',
    hasUploadedFile: false,
    importedAt: '2026-02-05T09:00:00Z',
    description: 'Australian standard for safe work in confined spaces, referenced by the WHS Regulations 2011. Mandatory for all cable pit entry activities.',
    keyRequirements: [
      'Atmospheric testing required before every entry: O2, LEL, CO, H2S',
      'Entry permit system: form must be completed and signed before each entry',
      'Standby person required at all times during entry',
      'Rescue equipment (tripod, winch, harness) must be in position before entry',
      'Re-test atmosphere if entry is suspended for more than 30 minutes',
    ],
    relatedSpecIds: [],
    linkedSopIds: ['wi-sop-009'],
    importedById: 'u2', importedByName: 'Jane Smith',
  },
]

// ── Helpers ───────────────────────────────────────────────────────

export function getSpecsBySource(source: SpecSource): ClientSpec[] {
  return MOCK_CLIENT_SPECS.filter(s => s.source === source)
}

export function getSpecsByDiscipline(discipline: WIDiscipline): ClientSpec[] {
  return MOCK_CLIENT_SPECS.filter(s => s.discipline === discipline || s.discipline === 'ALL')
}

export function getLinkedSpecs(sopId: string): ClientSpec[] {
  return MOCK_CLIENT_SPECS.filter(s => s.linkedSopIds.includes(sopId))
}

export function calcWILabourCost(
  steps: { estimatedHours?: number; tradeCode?: TradeCode }[]
): { byTrade: Record<string, { hours: number; cost: number }>; totalHours: number; totalCost: number } {
  const byTrade: Record<string, { hours: number; cost: number }> = {}
  let totalHours = 0
  let totalCost = 0

  for (const step of steps) {
    if (!step.estimatedHours || !step.tradeCode) continue
    const rate = TRADE_RATE_MAP[step.tradeCode]
    if (!rate) continue
    const cost = step.estimatedHours * rate.hourlyRateLoaded
    if (!byTrade[step.tradeCode]) byTrade[step.tradeCode] = { hours: 0, cost: 0 }
    byTrade[step.tradeCode].hours += step.estimatedHours
    byTrade[step.tradeCode].cost  += cost
    totalHours += step.estimatedHours
    totalCost  += cost
  }

  return {
    byTrade,
    totalHours: Math.round(totalHours * 100) / 100,
    totalCost:  Math.round(totalCost * 100) / 100,
  }
}
